import { SolarExamineReportService } from './solar.examine.report.service';
import { SolarRgbImageApi } from '../apis/solar.rgb.image.api';
import { SolarIrImageApi } from '../apis/solar.ir.image.api';
import { handleData } from './response.handler';
import { isRequestAborted, throwErrorIfFailed } from '@/utils/apiHelpers';
import { extractJobRunningStatus } from '@/utils/aiJobUtils';
import { AI_JOB_KINDS } from '@/constants/aiJobKinds';
import i18n from '@/plugins/i18n';

/**
 * AI 분석 작업 관련 서비스
 */
export const AiAnalysisService = {
  /**
   * 실행 중인 Job 상태 확인
   * @param {number} reportId - 보고서 ID
   * @returns {Promise<{isRgbRunning: boolean, isThermalRunning: boolean}>}
   */
  async checkRunningJobs(reportId) {
    try {
      const jobs = await SolarExamineReportService.getAiAnalysisStatus(reportId);
      return extractJobRunningStatus(jobs);
    } catch (error) {
      console.warn('Job 상태 확인 실패:', error);
      // 에러 발생 시 실행 중이 아닌 것으로 처리
      return {
        isRgbRunning: false,
        isThermalRunning: false
      };
    }
  },

  /**
   * 작업 실행 공통 로직
   * @param {number} reportId - 보고서 ID
   * @param {Array<string>} jobKinds - 실행할 Job 종류 배열
   * @param {string} workType - 작업 타입 ('RGB' 또는 'Thermal')
   * @returns {Promise<void>}
   * @throws {Error} 실행 중인 Job이 있거나 요청 실패 시
   * @private
   */
  async requestWork(reportId, jobKinds, workType) {
    const status = await this.checkRunningJobs(reportId);
    const isRunning = workType === 'RGB' ? status.isRgbRunning : status.isThermalRunning;

    if (isRunning) {
      throw new Error(i18n.t('aiJob.workAlreadyRunning', { workType }));
    }

    if (!jobKinds || jobKinds.length === 0) {
      throw new Error(i18n.t('aiJob.selectWork', { workType }));
    }

    return await SolarExamineReportService.requestAIAnalysisJob(reportId, jobKinds);
  },

  /**
   * 이미지 Geo-Localization 작업 실행
   * @param {number} reportId - 보고서 ID
   * @returns {Promise<void>}
   * @throws {Error} 실행 중인 Job이 있거나 요청 실패 시
   */
  async requestRgbWork(reportId) {
    return this.requestWork(reportId, [AI_JOB_KINDS.RGB_LOC], 'RGB');
  },

  /**
   * Thermal 작업 실행
   * @param {number} reportId - 보고서 ID
   * @param {Array<string>} jobKinds - 실행할 Thermal Job 종류 배열 (IR_LOC, IR_DATA, IR_DEFECT)
   * @returns {Promise<void>}
   * @throws {Error} 실행 중인 Job이 있거나 요청 실패 시
   */
  async requestThermalWork(reportId, jobKinds) {
    return this.requestWork(reportId, jobKinds, 'Thermal');
  },

  /**
   * AI Job 취소
   * @param {number} aiJobId - 취소할 AI Job ID
   * @returns {Promise<void>}
   * @throws {Error} 취소 실패 시
   */
  async cancelAiAnalysisJob(aiJobId, axiosConfig) {
    try {
      const response = await SolarExamineReportService.cancelAiAnalysisJob(aiJobId, axiosConfig);
      if (response && response.result === 'S') {
        return response;
      } else {
        throw new Error(response?.message || i18n.t('aiJob.jobCancelError'));
      }
    } catch (error) {
      console.error('AI 작업 취소 실패:', error);
      throw error;
    }
  },

  /**
   * AI Job 리스트 조회
   * @param {number} reportId - 보고서 ID
   * @returns {Promise<Array>} AI Job 리스트
   */
  async getAiAnalysisJobs(reportId, axiosConfig) {
    try {
      const response = await SolarExamineReportService.getAiAnalysisStatus(reportId, axiosConfig);
      const jobs = response?.content || (Array.isArray(response) ? response : []);
      return Array.isArray(jobs) ? jobs : [];
    } catch (error) {
      if (isRequestAborted(error)) {
        throw error;
      }
      console.warn('Job 리스트 조회 실패:', error);
      return [];
    }
  },

  /**
   * RGB 이미지 고도 데이터 조회
   * @param {number} reportId - 보고서 ID
   * @returns {Promise<{minAltitude: number, maxAltitude: number, avgAltitude: number}>} 고도 데이터
   */
  async getRgbImageAltitudeData(reportId, axiosConfig) {
    const response = await SolarRgbImageApi.getRgbImageAltitudes(reportId, axiosConfig);
    throwErrorIfFailed(response);
    const data = handleData(response);
    return data?.content ?? null;
  },

  /**
   * 열화상 이미지 고도 데이터 조회
   * @param {number} reportId - 보고서 ID
   * @returns {Promise<{minAltitude: number, maxAltitude: number, avgAltitude: number}>} 고도 데이터
   */
  async getThermalImageAltitudeData(reportId, axiosConfig) {
    const response = await SolarIrImageApi.getThermalImageAltitudes(reportId, axiosConfig);
    throwErrorIfFailed(response);
    const data = handleData(response);
    return data?.content ?? null;
  }
};

